import { DeleteOutlined } from '@ant-design/icons';
import { Popconfirm, Row } from 'antd';
import React from 'react';
import { Props } from '.';
import CapSelector from '../../../../components/cap-selector/CapSelector';
import Icon from '../../../../components/icon/Icon';
import ShotLocation from '../../../../components/shot-location/ShotLocation';
import ShotTarget from '../../../../components/shot-target/ShotTarget';
import {
    PENALTY_5M_CENTER_X,
    PENALTY_5M_CENTER_Y,
} from '../../../../constants/shotPenaltyLocation';
import { CapNumber } from '../../../../core/models/CapNumber';
import {
    filterLineupsForBlockedByPicker,
    getFieldPlayIneligibleCapNumbers,
    getTeamId,
    isShotOnTarget,
} from '../../../../helpers/EventHelper';
import {
    CreateMatchPlayerCommandOfMatchPlayer,
    EventVm,
    ShotRequest,
    ShotTypeEnum,
    UpdateEventCommand,
} from '../../../../utils/api';
import { ShotTechniqueEnum } from '../../../../utils/shotTechniqueEnum';

class Shot extends React.PureComponent<Props> {
    /** Keeps shotTechnique/fakeCount on partial updates; no technique selected (undefined) = direct. */
    private buildShotUpdate = (overrides: Partial<ShotRequest>): ShotRequest => {
        const { event } = this.props;
        const eventVm = event as EventVm;
        const s = event.shot!;
        return new ShotRequest({
            ...s,
            fakeCount: s.fakeCount ?? 0,
            ...overrides,
            id: eventVm.shotId || 0,
        });
    };

    public handleBlockedByPlayerSelect = (cap: number): void => {
        const { event, updateEvent, lineups } = this.props;

        const otherTeamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId !== getTeamId(event)
        );
        const lineup = otherTeamLineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.number === cap
        );
        const playerId = lineup?.id;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({
                blockedById: playerId,
                assistedById: undefined,
                savedById: undefined,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleBlockedByPlayerClear = (): void => {
        const { event, updateEvent } = this.props;

        const newShot = this.buildShotUpdate({
            blockedById: undefined,
            assistedById: undefined,
        });

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: new ShotRequest({
                ...newShot,
                savedById: this.calcSavedById(newShot),
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleAssistedByPlayerSelect = (cap: number): void => {
        const { event, updateEvent, lineups } = this.props;

        const teamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId === getTeamId(event)
        );
        const lineup = teamLineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.number === cap
        );
        const playerId = lineup?.id;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({
                assistedById: playerId,
                blockedById: undefined,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleAssistedByPlayerClear = (): void => {
        const { event, updateEvent } = this.props;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({
                assistedById: undefined,
                blockedById: undefined,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    /** Powerplay off ⇒ Regular_Attack (when not penalty). */
    private handlePowerplayToggle = (turnOn: boolean): void => {
        const t = this.props.event.shot?.typeId;
        if (t === ShotTypeEnum.Penalty || t === ShotTypeEnum.Penalty_Shootout) {
            return;
        }
        this.handleAttackTypeChange(turnOn ? ShotTypeEnum.Power_Play : ShotTypeEnum.Regular_Attack);
    };

    /** Penalty off ⇒ Regular_Attack (user re-enables PP separately if needed). */
    private handlePenaltyToggle = (turnOn: boolean): void => {
        const t = this.props.event.shot?.typeId;
        if (t === ShotTypeEnum.Penalty_Shootout) {
            return;
        }
        this.handleAttackTypeChange(turnOn ? ShotTypeEnum.Penalty : ShotTypeEnum.Regular_Attack);
    };

    public handleAttackTypeChange = (attackType: ShotTypeEnum): void => {
        this.props.onAttackTypeChange?.();
        const { event, updateEvent } = this.props;

        const isPenaltyType =
            attackType === ShotTypeEnum.Penalty || attackType === ShotTypeEnum.Penalty_Shootout;
        const fakeCount = isPenaltyType ? 0 : event.shot!.fakeCount ?? 0;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({
                typeId: attackType,
                isFastBreak: !isPenaltyType ? event.shot!.isFastBreak : false,
                isDirectFromFoul: !isPenaltyType ? event.shot!.isDirectFromFoul : false,
                fakeCount,
                ...(isPenaltyType
                    ? {
                          locationX: PENALTY_5M_CENTER_X,
                          locationY: PENALTY_5M_CENTER_Y,
                      }
                    : {}),
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleLobToggle = (checked: boolean): void => {
        const { event, updateEvent } = this.props;
        const s = event.shot!;
        const shotTechnique: number | undefined = checked
            ? ShotTechniqueEnum.Lob
            : s.shotTechnique === ShotTechniqueEnum.Skip
            ? ShotTechniqueEnum.Skip
            : undefined;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({ shotTechnique }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleSkipToggle = (checked: boolean): void => {
        const { event, updateEvent } = this.props;
        const s = event.shot!;
        const shotTechnique: number | undefined = checked
            ? ShotTechniqueEnum.Skip
            : s.shotTechnique === ShotTechniqueEnum.Lob
            ? ShotTechniqueEnum.Lob
            : undefined;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({ shotTechnique }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleOneFakeToggle = (checked: boolean): void => {
        const { event, updateEvent } = this.props;

        const isPenalty =
            event.shot?.typeId === ShotTypeEnum.Penalty ||
            event.shot?.typeId === ShotTypeEnum.Penalty_Shootout;
        if (isPenalty) {
            return;
        }

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({ fakeCount: checked ? 1 : 0 }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleMoreFakesToggle = (checked: boolean): void => {
        const { event, updateEvent } = this.props;

        const isPenalty =
            event.shot?.typeId === ShotTypeEnum.Penalty ||
            event.shot?.typeId === ShotTypeEnum.Penalty_Shootout;
        if (isPenalty) {
            return;
        }

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({ fakeCount: checked ? 2 : 0 }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleFastBreakToggle = (checked: boolean): void => {
        const { event, updateEvent } = this.props;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({
                isFastBreak: checked,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleDirectFromFoulToggle = (checked: boolean): void => {
        const { event, updateEvent } = this.props;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({
                isDirectFromFoul: checked,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleShotLocationSelect = (x: number, y: number): void => {
        const { event, updateEvent } = this.props;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({
                locationX: x,
                locationY: y,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleShotTargetSelect = (x: number, y: number): void => {
        const { event, updateEvent } = this.props;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            shot: this.buildShotUpdate({
                targetX: x,
                targetY: y,
                savedById: this.calcSavedById({
                    ...event.shot,
                    targetX: x,
                    targetY: y,
                } as ShotRequest),
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public calcSavedById = (shot: ShotRequest): number | undefined => {
        const { lineups, selectedHomeKeeper, selectedAwayKeeper, isHomeTeam } = this.props;

        if (shot.blockedById || shot.isGoal || !isShotOnTarget(shot)) {
            return;
        }

        // saved by keeper is in the team opposing the shot taker.
        // The caps are the goalkeepers at this event's clock, not the last change in the match.
        const savedByKeeperNumber = isHomeTeam ? selectedAwayKeeper : selectedHomeKeeper;

        return lineups.find((l) => l.number === savedByKeeperNumber && l.teamId !== shot.teamId)
            ?.id;
    };

    public render = (): React.ReactElement => {
        const {
            event,
            lineups,
            isHomeTeam,
            clearEvent,
            deleteEvent,
            penaltyShootoutRecordingMode,
            events,
            selectedHomeKeeper,
            selectedAwayKeeper,
        } = this.props;
        const eventVm = event as EventVm;
        const eventExists = !!eventVm.id;

        const teamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId === getTeamId(event)
        );
        const otherTeamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId !== getTeamId(event)
        );
        const activeDefendingKeeperCap = isHomeTeam ? selectedAwayKeeper : selectedHomeKeeper;
        const blockedByPickerLineups = filterLineupsForBlockedByPicker(
            otherTeamLineups,
            activeDefendingKeeperCap
        );
        const blockedByDisabledCaps = getFieldPlayIneligibleCapNumbers(
            events,
            blockedByPickerLineups
        );
        const assistedByDisabledCaps = getFieldPlayIneligibleCapNumbers(events, teamLineups);
        const shotType = event.shot?.typeId;

        const assistedById = event.shot?.assistedById;
        const assistedByPlayerLineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === assistedById
        );

        const blockedById = event.shot?.blockedById;
        const blockedByPlayerLineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === blockedById
        );

        const isFastBreak = event.shot?.isFastBreak;
        const isDirectFromFoul = event.shot?.isDirectFromFoul;

        const shotLocationX = event.shot?.locationX;
        const shotLocationY = event.shot?.locationY;

        const shotTargetX = event.shot?.targetX;
        const shotTargetY = event.shot?.targetY;

        const isGoal = event.shot?.isGoal as boolean;

        const isBlockedByDisabled =
            isGoal ||
            shotType === ShotTypeEnum.Penalty ||
            shotType === ShotTypeEnum.Penalty_Shootout;
        const isAssistedByDisabled =
            !isGoal ||
            shotType === ShotTypeEnum.Penalty ||
            shotType === ShotTypeEnum.Penalty_Shootout;
        const isPenalty =
            event.shot?.typeId === ShotTypeEnum.Penalty ||
            event.shot?.typeId === ShotTypeEnum.Penalty_Shootout;
        const rawTechnique = event.shot?.shotTechnique;
        const isLob = rawTechnique === ShotTechniqueEnum.Lob;
        const isSkip = rawTechnique === ShotTechniqueEnum.Skip;
        const rawFake = isPenalty ? 0 : event.shot?.fakeCount ?? 0;
        const isOneFake = rawFake === 1;
        const isMoreFakes = rawFake === 2;
        const pill = (
            label: string,
            active: boolean,
            onClick: () => void,
            disabled?: boolean,
            nonInteractive?: boolean
        ): React.ReactElement => (
            <span
                className={`c-shot-pill${active ? ' c-shot-pill--active' : ''}${
                    disabled ? ' c-shot-pill--disabled' : ''
                }${nonInteractive ? ' c-shot-pill--non-interactive' : ''}`}
                onClick={disabled || nonInteractive ? undefined : onClick}
                role="button"
            >
                {label}
            </span>
        );

        const activeShotType = event.shot?.typeId || ShotTypeEnum.Regular_Attack;

        const showCapsFirstLayout = !isBlockedByDisabled || !isAssistedByDisabled;

        return (
            <div className="event-create">
                <div
                    className={`c-shot-form__grid${
                        showCapsFirstLayout ? ' c-shot-form__grid--caps-first' : ''
                    }`}
                >
                    {showCapsFirstLayout && (
                        <div className="c-shot-form__caps">
                            {!isBlockedByDisabled && (
                                <>
                                    <div className="c-shot-form__caps-label">Blocked by</div>
                                    <CapSelector
                                        capClass={`c-shot-form__cap-selector c-shot-form__cap-selector--${
                                            isHomeTeam ? 'away' : 'home'
                                        }`}
                                        onSelect={this.handleBlockedByPlayerSelect}
                                        onClear={this.handleBlockedByPlayerClear}
                                        caps={blockedByPickerLineups.map(
                                            (
                                                l: CreateMatchPlayerCommandOfMatchPlayer
                                            ): CapNumber => ({
                                                cap: l.number,
                                                capNumber: l.capNumber || l.number,
                                            })
                                        )}
                                        activeCap={
                                            blockedByPlayerLineup
                                                ? blockedByPlayerLineup.number
                                                : null
                                        }
                                        activeCapNumber={
                                            blockedByPlayerLineup
                                                ? blockedByPlayerLineup.capNumber ||
                                                  blockedByPlayerLineup.number
                                                : null
                                        }
                                        disabledCaps={blockedByDisabledCaps}
                                    />
                                </>
                            )}
                            {!isAssistedByDisabled && (
                                <>
                                    <div className="c-shot-form__caps-label">Assisted by</div>
                                    <CapSelector
                                        capClass={`c-shot-form__cap-selector c-shot-form__cap-selector--${
                                            isHomeTeam ? 'home' : 'away'
                                        }`}
                                        onSelect={this.handleAssistedByPlayerSelect}
                                        onClear={this.handleAssistedByPlayerClear}
                                        caps={teamLineups.map(
                                            (
                                                l: CreateMatchPlayerCommandOfMatchPlayer
                                            ): CapNumber => ({
                                                cap: l.number,
                                                capNumber: l.capNumber || l.number,
                                            })
                                        )}
                                        activeCap={
                                            assistedByPlayerLineup
                                                ? assistedByPlayerLineup.number
                                                : null
                                        }
                                        activeCapNumber={
                                            assistedByPlayerLineup
                                                ? assistedByPlayerLineup.capNumber ||
                                                  assistedByPlayerLineup.number
                                                : null
                                        }
                                        disabledCaps={assistedByDisabledCaps}
                                    />
                                </>
                            )}
                        </div>
                    )}
                    <div className="c-shot-form__pills-cluster">
                        {!penaltyShootoutRecordingMode && (
                            <>
                                <div className="c-shot-form__col">
                                    {pill(
                                        'Powerplay',
                                        activeShotType === ShotTypeEnum.Power_Play,
                                        () =>
                                            this.handlePowerplayToggle(
                                                activeShotType !== ShotTypeEnum.Power_Play
                                            ),
                                        activeShotType === ShotTypeEnum.Penalty ||
                                            activeShotType === ShotTypeEnum.Penalty_Shootout
                                    )}
                                    {pill('Penalty', activeShotType === ShotTypeEnum.Penalty, () =>
                                        this.handlePenaltyToggle(
                                            activeShotType !== ShotTypeEnum.Penalty
                                        )
                                    )}
                                </div>
                                <div className="c-shot-form__col">
                                    {pill(
                                        'Fast Break',
                                        !!isFastBreak,
                                        () => this.handleFastBreakToggle(!isFastBreak),
                                        isPenalty
                                    )}
                                    {pill(
                                        '6m foul',
                                        !!isDirectFromFoul,
                                        () => this.handleDirectFromFoulToggle(!isDirectFromFoul),
                                        isPenalty
                                    )}
                                </div>
                            </>
                        )}
                        {penaltyShootoutRecordingMode && (
                            <div className="c-shot-form__col">
                                {pill(
                                    'Pen SO',
                                    activeShotType === ShotTypeEnum.Penalty_Shootout,
                                    () =>
                                        this.handleAttackTypeChange(ShotTypeEnum.Penalty_Shootout),
                                    false,
                                    true
                                )}
                            </div>
                        )}
                        <div className="c-shot-form__col">
                            {pill('Skip Shot', isSkip, () => this.handleSkipToggle(!isSkip))}
                            {pill('Lob Shot', isLob, () => this.handleLobToggle(!isLob))}
                        </div>
                        {!penaltyShootoutRecordingMode && (
                            <div className="c-shot-form__col">
                                {pill(
                                    '1 Fake',
                                    isOneFake,
                                    () => this.handleOneFakeToggle(!isOneFake),
                                    isPenalty
                                )}
                                {pill(
                                    'More Fakes',
                                    isMoreFakes,
                                    () => this.handleMoreFakesToggle(!isMoreFakes),
                                    isPenalty
                                )}
                            </div>
                        )}
                    </div>
                    <div className="c-shot-form__actions">
                        <span
                            className="c-shot-form__action-btn"
                            onClick={(): void => clearEvent()}
                            role="button"
                            title="Close"
                        >
                            <Icon name="icon-times" />
                        </span>
                        {eventExists && (
                            <Popconfirm
                                title="Are you sure you want to delete this event?"
                                onConfirm={(): void => deleteEvent(eventVm)}
                                okText="Delete"
                                okButtonProps={{ danger: true }}
                                cancelText="Cancel"
                            >
                                <span
                                    className="c-shot-form__action-btn c-shot-form__action-btn--delete"
                                    role="button"
                                    title="Delete"
                                >
                                    <DeleteOutlined />
                                </span>
                            </Popconfirm>
                        )}
                    </div>
                </div>
                <Row>
                    <ShotTarget
                        isGoal={isGoal}
                        x={shotTargetX}
                        y={shotTargetY}
                        onSelect={this.handleShotTargetSelect}
                    />
                </Row>
                <Row
                    style={{
                        display: 'inline-flex',
                        marginBottom: 20,
                    }}
                >
                    <ShotLocation
                        x={shotLocationX}
                        y={shotLocationY}
                        onSelect={this.handleShotLocationSelect}
                    />
                </Row>
            </div>
        );
    };
}

export default Shot;
